import { Fragment, type ReactNode } from "react";

// Tiny inline-markdown renderer for our LLM-emitted prose. Only handles
// `**bold**`, `*italic*`, and a couple of automatic patterns we want to
// look extra-pretty:
//   - dollar amounts ("$683.00") rendered in subtly warmer ink
//   - 4-digit years ("2015", "2023") nudged toward the Fraunces variation
//     and lightly tinted, so the timeline reads like landmarks
//
// Keep this fast and forgiving - if the LLM forgets to close an asterisk
// we just render the literal characters rather than crashing the page.

type Token =
  | { type: "text"; value: string }
  | { type: "bold"; children: Token[] }
  | { type: "italic"; children: Token[] };

/** Tokenize a flat string into bold / italic / text segments. */
function tokenize(input: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < input.length) {
    // **bold**
    if (input[i] === "*" && input[i + 1] === "*") {
      const end = input.indexOf("**", i + 2);
      if (end > i + 2) {
        out.push({ type: "bold", children: tokenize(input.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    // *italic* - must not be a leading * before whitespace (would be a list-ish artifact)
    if (input[i] === "*" && input[i + 1] !== "*") {
      const end = input.indexOf("*", i + 1);
      // Avoid eating asterisks across paragraph breaks (sloppy LLM output).
      const slice = end > i + 1 ? input.slice(i + 1, end) : "";
      if (end > i + 1 && !slice.includes("\n") && slice.trim().length > 0) {
        out.push({ type: "italic", children: tokenize(slice) });
        i = end + 1;
        continue;
      }
    }
    // Plain run - accumulate until the next * marker (or end).
    //
    // BUG-FIX (Tyrone James page hang, 2026-05-03): if `i` is sitting ON
    // a `*` that didn't form a valid bold/italic pair above (e.g. the
    // second `*` in unbalanced `**` when the LLM emits malformed
    // markdown like "Paterson** to **multiple"), then indexOf("*", i)
    // returns `i` itself - so stop===i, we push an empty text node, and
    // i never advances. Infinite loop, frozen renderer.
    //
    // Fix: search for the NEXT `*` strictly after the current position.
    // The current `*` falls through into the plain run as a literal - the
    // intended behavior anyway when neither bold nor italic matched.
    const startSearch = input[i] === "*" ? i + 1 : i;
    const next = input.indexOf("*", startSearch);
    const stop = next === -1 ? input.length : next;
    out.push({ type: "text", value: input.slice(i, Math.max(stop, i + 1)) });
    i = Math.max(stop, i + 1);
  }
  return out;
}

// Decorate plain text runs with auto-emphasis for dollar amounts + years.
// Returns a list of React nodes.
const DOLLAR_RE = /\$\s?\d+(?:,\d{3})*(?:\.\d{2})?/g;
const YEAR_RE = /\b(19\d{2}|20\d{2})\b/g;

function decoratePlain(text: string, keyPrefix: string): ReactNode[] {
  // Two-pass: first split on dollars (priority), then split each non-dollar
  // chunk on year matches.
  const out: ReactNode[] = [];
  let chunkIdx = 0;
  const dollarSplit = splitWithRegex(text, DOLLAR_RE);
  for (const piece of dollarSplit) {
    if (piece.match) {
      out.push(
        <span
          key={`${keyPrefix}-d-${chunkIdx++}`}
          style={{
            color: "var(--color-gold-hi)",
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
          }}
        >
          {piece.text}
        </span>,
      );
      continue;
    }
    // Now split this non-dollar piece on years.
    const yearSplit = splitWithRegex(piece.text, YEAR_RE);
    for (const sub of yearSplit) {
      if (sub.match) {
        out.push(
          <span
            key={`${keyPrefix}-y-${chunkIdx++}`}
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              color: "var(--color-rose-hi)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {sub.text}
          </span>,
        );
      } else if (sub.text) {
        out.push(<Fragment key={`${keyPrefix}-t-${chunkIdx++}`}>{sub.text}</Fragment>);
      }
    }
  }
  return out;
}

function splitWithRegex(input: string, re: RegExp): Array<{ text: string; match: boolean }> {
  const result: Array<{ text: string; match: boolean }> = [];
  let lastIdx = 0;
  // Use a fresh regex so global state doesn't bleed across calls.
  const r = new RegExp(re.source, re.flags);
  for (const m of input.matchAll(r)) {
    if (m.index === undefined) continue;
    if (m.index > lastIdx) result.push({ text: input.slice(lastIdx, m.index), match: false });
    result.push({ text: m[0], match: true });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < input.length) result.push({ text: input.slice(lastIdx), match: false });
  return result;
}

function renderTokens(tokens: Token[], keyPrefix = "t"): ReactNode[] {
  return tokens.map((tok, i) => {
    const k = `${keyPrefix}-${i}`;
    if (tok.type === "text") return <Fragment key={k}>{decoratePlain(tok.value, k)}</Fragment>;
    if (tok.type === "bold")
      return (
        <strong key={k} style={{ fontWeight: 700, color: "var(--color-text)" }}>
          {renderTokens(tok.children, k)}
        </strong>
      );
    return (
      <em
        key={k}
        style={{
          fontStyle: "italic",
          fontFamily: "var(--font-serif)",
          color: "var(--color-text-muted)",
        }}
      >
        {renderTokens(tok.children, k)}
      </em>
    );
  });
}

/** Render an inline-markdown string as React nodes. */
export function renderInlineMarkdown(input: string): ReactNode {
  return renderTokens(tokenize(input));
}
